import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { default as classNames, default as classnames } from 'classnames';
import { isEmpty, map } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { applySchemaSpec } from '../../actions';
import {
  SelectInput,
  SelectOption,
  getCurrentSelectedOption,
  getNewSelectionValue,
  recipeBuilderSelectStyles,
} from '../elements/rselect/ReactSelectUtils';
import SortedSelect from '../elements/rselect/SortedSelect';
import CollapseIcon from './icons/CollapseIcon';
import ExpandIcon from './icons/ExpandIcon';
import { OUTPUT_SCHEMA_FROM_SOURCE_SAMPLE_ENABLED } from 'js/constants/config';
import { NexsetSampleViewGrid } from './NexsetSampleViewGrid';
import { Grid } from './SchemaSpecGrid';

const MODE_SCHEMA_SPEC = 'Schema Template';
const MODE_NEXSET_SAMPLE = 'Nexset Sample';
const MODE_SOURCE_SAMPLE = 'Source Sample';
const MODE_DESTINATION_SAMPLE = 'Destination Sample';

const MODES = ['Schema Template', 'Nexset Sample', 'Source Sample', 'Destination Sample'];

const isModeEnabled = (value) => {
  switch (value) {
    case MODE_SOURCE_SAMPLE:
      return OUTPUT_SCHEMA_FROM_SOURCE_SAMPLE_ENABLED;

    default:
      return true;
  }
};

const SchemaSpecSelector = ({ dataSet, outputSchema }) => {
  const destinationDraft = useSelector((state: any) => state.recipeBuilder.destinationDraft);
  const [isExpanded, setIsExpanded] = useState(false);
  const [mode, setMode] = useState<string | null>(null);

  const onHeaderClicked = () => {
    setIsExpanded(!isExpanded);
  };

  const onBackClicked = (event) => {
    event.stopPropagation();

    setMode(null);
  };

  useEffect(() => {
    if (!isEmpty(destinationDraft)) {
      setMode(MODE_DESTINATION_SAMPLE);
      setIsExpanded(true);
    }
  }, []);

  return (
    <div
      className={classnames('recipe-builder-schema-spec-selector', {
        'is-expanded': isExpanded,
      })}
    >
      <div className="schema-spec-selector-header" onClick={onHeaderClicked}>
        <ChevronLeftIcon className={classNames({ invisible: !mode })} onClick={onBackClicked} />
        {mode || 'Output Schema Library'}
        {isExpanded ? <ExpandIcon /> : <CollapseIcon />}
      </div>
      {isExpanded && (
        <ExpandedContent
          dataSet={dataSet}
          outputSchema={outputSchema}
          mode={mode}
          setMode={setMode}
        />
      )}
    </div>
  );
};

const ExpandedContent = ({ dataSet, outputSchema, mode, setMode }) => {
  const destinationDraft = useSelector((state: any) => state.recipeBuilder.destinationDraft);

  const onButtonClicked = (event) => {
    const { value } = event.currentTarget.dataset;

    setMode(value);
  };

  const isModeDisabled = (value) => {
    switch (value) {
      case MODE_DESTINATION_SAMPLE:
        return isEmpty(destinationDraft);

      default:
        return false;
    }
  };

  const isModeActive = (value) => {
    switch (value) {
      case MODE_DESTINATION_SAMPLE:
        return !isEmpty(destinationDraft);

      default:
        return false;
    }
  };

  switch (mode) {
    case MODE_SCHEMA_SPEC:
      return <SchemaSpecView outputSchema={outputSchema} />;

    case MODE_DESTINATION_SAMPLE:
      return <DestinationDraftView outputSchema={outputSchema} />;

    case MODE_NEXSET_SAMPLE:
      return <NexsetSampleViewSelector dataSet={dataSet} outputSchema={outputSchema} />;
  }

  return (
    <>
      {MODES.map(
        (value) =>
          isModeEnabled(value) && (
            <button
              key={value}
              data-value={value}
              className="schema-spec-selector-mode-button"
              disabled={isModeDisabled(value)}
              onClick={onButtonClicked}
            >
              <span className="button-label-container">
                {isModeActive(value) && <div className="active-mode-indicator" />}
                {value}
              </span>
              <ChevronRightIcon />
            </button>
          ),
      )}
    </>
  );
};

const NexsetSampleViewSelector = ({ dataSet, outputSchema }) => {
  return <NexsetSampleViewGrid dataSet={dataSet} outputSchema={outputSchema} />;
};

const DestinationDraftView = ({ outputSchema }) => {
  const destinationDraft = useSelector((state: any) => state.recipeBuilder.destinationDraft);

  return (
    <div className="schema-spec-selector-content">
      {!isEmpty(destinationDraft) && (
        <Grid
          schema={destinationDraft.schema}
          samples={destinationDraft.samples}
          rawSchema={destinationDraft.rawSchema}
          rawSamples={destinationDraft.rawSamples}
          outputSchema={outputSchema}
          allowSaving
        />
      )}
    </div>
  );
};

const SchemaSpecView = ({ outputSchema }) => {
  const dispatch = useDispatch();
  const schemaSpecs = useSelector((state: any) => state.entities.schemaSpecs);
  const sortedIds = useSelector((state: any) => state.schemaSpecs.sortedIds);
  const sortedPublicIds = useSelector((state: any) => state.schemaSpecs.sortedPublicIds);
  const flatSchemasBySpecId = useSelector((state: any) => state.schemaSpecs.flatSchemas);
  const flatSamplesBySpecId = useSelector((state: any) => state.schemaSpecs.flatSamples);
  const appliedSchemaSpecId = useSelector((state: any) => state.recipeBuilder.appliedSchemaSpecId);
  const sortedSchemaSpecs = map(sortedIds, (id) => schemaSpecs[id]);
  const sortedPublicSchemaSpecs = map(sortedPublicIds, (id) => schemaSpecs[id]);

  const handleSchemaSpecChanged = (selectedOption) => {
    const schemaSpecId = getNewSelectionValue(selectedOption, true);
    dispatch(applySchemaSpec(schemaSpecId));
  };

  const ownSchemaSpecOptions = map(sortedSchemaSpecs, (spec) => ({
    label: spec.name,
    value: spec.id,
  }));
  const publicSchemaSpecOptions = map(sortedPublicSchemaSpecs, (spec) => ({
    label: spec.name,
    value: spec.id,
  }));
  const allSchemaSpecOptions = [...ownSchemaSpecOptions, ...publicSchemaSpecOptions];
  const schemaSpecOptions = [];

  if (!isEmpty(ownSchemaSpecOptions)) {
    schemaSpecOptions.push({
      label: 'Owned by Me',
      options: ownSchemaSpecOptions,
    });
  }

  if (!isEmpty(publicSchemaSpecOptions)) {
    schemaSpecOptions.push({
      label: 'Public',
      options: publicSchemaSpecOptions,
    });
  }

  const appliedSchemaSpecOption = getCurrentSelectedOption(
    allSchemaSpecOptions,
    appliedSchemaSpecId,
  );

  return (
    <div className="schema-spec-selector-content">
      <SortedSelect
        placeholder="Select Schema Template"
        value={appliedSchemaSpecOption}
        options={schemaSpecOptions}
        components={{ Input: SelectInput, Option: SelectOption }}
        isClearable={true}
        backspaceRemovesValue={true}
        isSearchable={true}
        styles={{
          ...recipeBuilderSelectStyles,
          container: (styles) => ({
            ...styles,
            fontSize: 12,
          }),
          control: (styles) => ({
            ...styles,
            minHeight: 24,
            flexWrap: 'nowrap',
            background: '#fff',
            border: '1px solid #DFE8F0',
            cursor: 'pointer',
          }),
        }}
        onChange={handleSchemaSpecChanged}
      />
      {appliedSchemaSpecId && (
        <Grid
          schema={flatSchemasBySpecId[appliedSchemaSpecId]}
          samples={flatSamplesBySpecId[appliedSchemaSpecId]}
          outputSchema={outputSchema}
        />
      )}
    </div>
  );
};

export default SchemaSpecSelector;
